import {
  Card,
  CardBody,
  CardHeader,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useFetchAfeDetailsQuery } from 'api';
import Decimal from 'decimal.js';
import { CurrencyLabel, FormButtonGroup } from 'features/components';
import { uniqBy } from 'lodash';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { WorkOrderWorkFlowStage } from './WorkOrderFormWorkflowDrawer';
import { PostWorkOrderRequest } from 'api/bindings/PostWorkOrderRequest';
import { PutWorkOrderRequest } from 'api/bindings/PutWorkOrderRequest';
import { PostWorkOrderTransactionRequest } from 'api/bindings/PostWorkOrderTransactionRequest';

type WorkOrderModel = PostWorkOrderRequest | PutWorkOrderRequest;
export interface WorkOrderFormReviewDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit'> {
  afeId: string;
  initialStage: WorkOrderWorkFlowStage;
  workOrder: WorkOrderModel;
  revision?: PostWorkOrderTransactionRequest;
  onSubmit: (form: Form) => void;
  onClose: () => void;
}

interface Form {
  nextStage?: WorkOrderWorkFlowStage;
}

export const WorkOrderFormReviewDrawerContent = (
  props: WorkOrderFormReviewDrawerContentProps
) => {
  const {
    afeId,
    initialStage,
    workOrder,
    revision,
    onSubmit,
    onClose,
    ...rest
  } = props;

  const form = useForm<Form>({ mode: 'all' });
  const { control, formState, handleSubmit } = form;

  const { data: afe } = useFetchAfeDetailsQuery(afeId, {
    skip: !afeId,
  });

  const revisionStats = useMemo(() => {
    if (!revision) return undefined;
    return {
      total: revision.accounts
        .map((a) => new Decimal(a.amount))
        .reduce((sum, n) => sum.add(n), new Decimal(0)),
      subAccountCount: uniqBy(revision.accounts, (x) => x.subAccountId).length,
    };
  }, [revision]);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerContent {...rest}>
          <DrawerCloseButton />
          <DrawerHeader>Review Submission: {workOrder.name}</DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              {initialStage === WorkOrderWorkFlowStage.workOrder && (
                <Card>
                  <CardHeader>
                    <Text as="b">Work Order</Text>
                  </CardHeader>
                  <CardBody>
                    <SimpleGrid columns={2} gap={2}>
                      <Text as="b">AFE:</Text>
                      <Text>{afe?.name}</Text>
                      <Text as="b">Work Order Name:</Text>
                      <Text>{workOrder.name}</Text>
                    </SimpleGrid>
                  </CardBody>
                </Card>
              )}
              {revision && (
                <Card>
                  <CardHeader>
                    <Text as="b">New Revision</Text>
                  </CardHeader>
                  <CardBody>
                    <SimpleGrid columns={2} gap={2}>
                      <Text as="b">Total:</Text>
                      <CurrencyLabel value={revisionStats?.total} />
                      <Text as="b">Accounts:</Text>
                      <Text>{revisionStats?.subAccountCount}</Text>
                    </SimpleGrid>
                  </CardBody>
                </Card>
              )}
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              onCancel={onClose}
              onConfirm={handleSubmit(onSubmit)}
              disabledWhenClean={false}
            />
          </DrawerFooter>
        </DrawerContent>
      </form>
    </FormProvider>
  );
};
